import {
  IAirTableBasesResponseDataModel,
  IAirTableTablesResponseDataModel,
} from '../../data-models/airtable-base.data-model';
import {
  IAirTableEntityDataModel,
  IForesightFieldDataModel,
} from '../../data-models/airtable-entity.data-model';
import {
  IAirtableSyncDataModel,
  IAirtableSyncPayloadDataModel,
  IEstablishedSyncDataModel,
  ISyncColumnsDataModel,
} from '../../data-models/airtable-sync.data-model';
import { IIntegrationsAppDataModel } from '../../data-models/integrations-app.data-model';
import {
  SlackChannel,
  SlackChannelSummary,
  SyncSlackChannelPayload,
} from '../../schemas/SlackChannel.schema';
import { MaggieHttp } from '../MaggieHttp';

export async function fetchIntegrations() {
  const { data } = await MaggieHttp.maggie.get<IIntegrationsAppDataModel[]>(`/integrations`, {
    params: {
      callback: window.location.href,
    },
  });
  return data;
}

export async function disconnectIntegration(integrationId: number) {
  const { data } = await MaggieHttp.maggie.delete(`/integrations/${integrationId}`);
  return data;
}

export async function reconnectAirtable(callback?: string) {
  return await MaggieHttp.maggie.get<IIntegrationsAppDataModel>(`/airtable/oauth/reconnect`, {
    params: {
      callback,
    },
  });
}

export async function fetchConnectedBases() {
  const { data } = await MaggieHttp.maggie.get<IAirTableBasesResponseDataModel>(`/airtable/base`);
  return data;
}

export async function fetchTablesForBase(baseId: string) {
  const { data } = await MaggieHttp.maggie.get<IAirTableTablesResponseDataModel>(`/airtable/base/${baseId}`);
  return data;
}

export async function fetchAirtableEntities() {
  const { data } = await MaggieHttp.maggie.get<IAirTableEntityDataModel[]>(`/airtable/sync/entities`);
  return data;
}

export async function createAirtableSync(payload: IAirtableSyncPayloadDataModel) {
  const { data } = await MaggieHttp.maggie.post<IAirtableSyncPayloadDataModel, IAirtableSyncDataModel>(
    `/airtable/sync`,
    payload
  );
  return data;
}

export async function fetchSyncColumns({
  baseId,
  tableId,
  entity,
}: {
  baseId: string;
  tableId: string;
  entity?: string;
}) {
  const { data } = await MaggieHttp.maggie.get<ISyncColumnsDataModel>(`/airtable/sync/columns`, {
    params: {
      baseId,
      tableId,
      entity,
    },
  });
  return data;
}

export async function fetchEstablishedSyncs() {
  const { data } = await MaggieHttp.maggie.get<IEstablishedSyncDataModel[]>(`/airtable/sync`);
  return data;
}

export async function deleteEstablishedSync(id: number) {
  const { data } = await MaggieHttp.maggie.delete<IEstablishedSyncDataModel>(`/airtable/sync/${id}`);
  return data;
}

export async function deleteEstablishedColumnSync(id: number) {
  const { data } = await MaggieHttp.maggie.delete<ISyncColumnsDataModel>(`/airtable/sync/columns/${id}`);
  return data;
}

export async function fetchAllFDFields() {
  const { data } = await MaggieHttp.maggie.get<IForesightFieldDataModel[]>(`/airtable/sync/columns`);
  return data;
}

export async function fetchAllSlackChannels() {
  const { data } = await MaggieHttp.maggie.get<SlackChannel[]>(`/slack/channels`);
  return data;
}

export async function syncSlackChannel(payload: SyncSlackChannelPayload) {
  await MaggieHttp.maggie.put<SyncSlackChannelPayload, void>(`/slack/syncChannel`, payload);
}

export async function fetchSlackSummary(companyId: number) {
  const { data } = await MaggieHttp.maggie.get<SlackChannelSummary>(`/slack/summary/${companyId}`);
  return data;
}

export async function updateSlackSummary(companyId: number) {
  const { data } = await MaggieHttp.maggie.put<void, { updated: boolean }>(`/slack/syncChannel/${companyId}`);
  return data;
}

// Affinity Integration APIs
export interface ConfigureAffinityPayload {
  apiKey: string;
}

export async function configureAffinity(payload: ConfigureAffinityPayload) {
  const { data } = await MaggieHttp.maggie.post<
    ConfigureAffinityPayload,
    { success: boolean; message: string }
  >(`/integrations/affinity/configure`, payload);
  return data;
}

// AllVue Integration APIs
export interface AllVueConfig {
  connectionType: 'snowflake' | 'oauth' | 'not_configured';
  isConnected: boolean;
  config: {
    database?: string;
    clientId?: string;
    tokenEndpoint?: string;
  } | null;
}

export interface ConfigureAllVueOAuthPayload {
  clientId: string;
  clientSecret?: string;
  tokenEndpoint?: string;
}

export interface ConfigureAllVueSnowflakePayload {
  database: string;
}

export async function fetchAllVueConfig() {
  const { data } = await MaggieHttp.maggie.get<AllVueConfig>(`/integrations/allvue/config`);
  return data;
}

export async function configureAllVueOAuth(payload: ConfigureAllVueOAuthPayload) {
  const { data } = await MaggieHttp.maggie.post<
    ConfigureAllVueOAuthPayload,
    { success: boolean; message: string; connectionType: string }
  >(`/integrations/allvue/configure/oauth`, payload);
  return data;
}

export async function configureAllVueSnowflake(payload: ConfigureAllVueSnowflakePayload) {
  const { data } = await MaggieHttp.maggie.post<
    ConfigureAllVueSnowflakePayload,
    { success: boolean; message: string; connectionType: string }
  >(`/integrations/allvue/configure/snowflake`, payload);
  return data;
}

// Ark PES Integration APIs
export interface ArkPesConfig {
  isConnected: boolean;
  config: {
    clientId?: string;
  } | null;
}

export interface ConfigureArkPesPayload {
  clientId: string;
}

export async function fetchArkPesConfig() {
  const { data } = await MaggieHttp.maggie.get<ArkPesConfig>(`/integrations/arkpes/config`);
  return data;
}

export async function configureArkPes(payload: ConfigureArkPesPayload) {
  const { data } = await MaggieHttp.maggie.post<
    ConfigureArkPesPayload,
    { success: boolean; message: string }
  >(`/integrations/arkpes/configure`, payload);
  return data;
}

// iLevel Integration APIs
export interface ILevelConfig {
  isConnected: boolean;
  config: {
    clientId?: string;
  } | null;
}

export interface ConfigureILevelPayload {
  clientId: string;
  clientSecret?: string;
}

export async function fetchILevelConfig() {
  const { data } = await MaggieHttp.maggie.get<ILevelConfig>(`/integrations/ilevel/config`);
  return data;
}

export async function configureILevel(payload: ConfigureILevelPayload) {
  const { data } = await MaggieHttp.maggie.post<
    ConfigureILevelPayload,
    { success: boolean; message: string }
  >(`/integrations/ilevel/configure`, payload);
  return data;
}

// QuickBooks Integration APIs
export interface QuickBooksFundConnection {
  fundId: number;
  fundName: string;
  isConnected: boolean;
}

export interface QuickBooksConfig {
  isConnected: boolean;
  funds: QuickBooksFundConnection[];
}

export async function fetchQuickBooksConfig() {
  const { data } = await MaggieHttp.maggie.get<QuickBooksConfig>(`/quickbooks/config`);
  return data;
}

export async function fetchQuickBooksAuthUri(fundId: number) {
  const { data } = await MaggieHttp.maggie.get<{ url: string }>(`/quickbooks/authUri`, {
    params: { fundId },
  });
  return data;
}

export async function disconnectQuickBooksFund(fundId: number) {
  const { data } = await MaggieHttp.maggie.delete<{ success: boolean }>(`/quickbooks/connections/${fundId}`);
  return data;
}

// Standard Metrics Integration APIs
export interface StandardMetricsConfig {
  isConnected: boolean;
  config: {
    clientId?: string;
  } | null;
}

export interface ConfigureStandardMetricsPayload {
  clientId: string;
  clientSecret?: string;
}

export async function fetchStandardMetricsConfig() {
  const { data } = await MaggieHttp.maggie.get<StandardMetricsConfig>(
    `/integrations/standard-metrics/config`
  );
  return data;
}

export async function configureStandardMetrics(payload: ConfigureStandardMetricsPayload) {
  const { data } = await MaggieHttp.maggie.post<
    ConfigureStandardMetricsPayload,
    { success: boolean; message: string }
  >(`/integrations/standard-metrics/configure`, payload);
  return data;
}

// Cobalt Integration APIs
export interface CobaltConfig {
  isConnected: boolean;
  config: {
    apiKeyLast4?: string | null;
  } | null;
}

export interface ConfigureCobaltPayload {
  apiKey?: string;
}

export async function fetchCobaltConfig() {
  const { data } = await MaggieHttp.maggie.get<CobaltConfig>(`/integrations/cobalt/config`);
  return data;
}

export async function configureCobalt(payload: ConfigureCobaltPayload) {
  const { data } = await MaggieHttp.maggie.post<
    ConfigureCobaltPayload,
    { success: boolean; message: string }
  >(`/integrations/cobalt/configure`, payload);
  return data;
}
